/**
 * VideoCallAnalyticsPage.tsx
 *
 * "Video Calls > Analytics" — account-wide rollup of the AI scorecard feature.
 * Every video call is scored automatically once its transcript is ready
 * (GenerateVideoCallScorecardJob), and this page aggregates those scores into
 * a daily trend chart plus a per-team-member breakdown across the active
 * scorecard template's fields (GET /api/v5/video-calls/analytics).
 *
 * Two filters sit above the chart:
 *  - "Scorecard Average" category dropdown — switches which metric (overall
 *    or a single field) is plotted. Pure client-side re-render; the fetched
 *    payload already carries per-field averages for every day/user.
 *  - "People" multi-select — draws one chart line per selected person (plus
 *    an optional "Team Average" line, itself just another selectable entry),
 *    instead of a single flat average.
 *
 * The section is open to all staff, but the API decides how much of it each
 * person may see: below the manager level it returns only their own calls and
 * marks the payload `scope: 'self'`. That view drops the People filter and the
 * "Total" row rather than renaming them — a "Team Average" that is one person's
 * average, or a "Total" identical to the only row beneath it, reads as team
 * data the viewer is not actually being shown.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { PageHeader } from '@/components/layout/PageHeader';
import { Select } from '@/components/ui/Select';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { usePageContext } from '@/hooks/usePageContext';
import { getFilterOptions } from '@/services/filters.service';
import * as videoCallsSvc from '@/services/videoCalls.service';
import type {
  VideoCallAnalyticsUserRow,
  VideoCallAnalyticsFieldScore,
  VideoCallAnalyticsTrendPoint,
} from '@/services/videoCalls.service';
import { DatePicker } from '@/components/ui/DatePicker';

/** Pinned sentinel entry in the People list — not a real userID. */
const TEAM_ID = 'team';
const TEAM_LABEL = 'Team Average';
/** Same single line, when the payload covers only the signed-in user's calls. */
const SELF_LABEL = 'My Average';

const LINE_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#14b8a6'];

function formatAxisDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Default date range: 7 days ago to today (YYYY-MM-DD). */
function defaultDateRange(): { dateFrom: string; dateTo: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return {
    dateFrom: start.toISOString().slice(0, 10),
    dateTo: end.toISOString().slice(0, 10),
  };
}

function scoreToneClass(score: number | null, ratingMax: number): string {
  if (score === null) return 'bg-slate-50 text-slate-400';
  const pct = ratingMax > 0 ? score / ratingMax : 0;
  if (pct >= 0.8) return 'bg-[#1f4d3d] text-white';
  if (pct >= 0.6) return 'bg-[#4a3c28] text-white';
  return 'bg-[#5c1f2e] text-white';
}

const thCls =
  'border border-slate-200 px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap';
const nameTdCls = 'border border-slate-200 px-4 py-3 text-sm';

function ScoreCell({ score, ratingMax }: { score: number | null; ratingMax: number }) {
  return (
    <td className={`border border-slate-200 px-4 py-3 text-sm font-semibold ${scoreToneClass(score, ratingMax)}`}>
      {score === null ? '—' : score.toFixed(1)}
    </td>
  );
}

function fieldScore(fields: VideoCallAnalyticsFieldScore[], fieldId: number): number | null {
  return fields.find((f) => f.fieldId === fieldId)?.score ?? null;
}

/** Pull whichever metric is currently selected out of one trend point. */
function metricValue(point: VideoCallAnalyticsTrendPoint, metric: string): number | null {
  if (metric === 'overall') return point.avgOverallScore;
  return fieldScore(point.fields, Number(metric));
}

export function VideoCallAnalyticsPage() {
  const updateAIContext = usePageContext('Video Calls Analytics');
  const [{ dateFrom, dateTo }, setRange] = useState(defaultDateRange());
  const [metric, setMetric] = useState<string>('overall');
  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ['video-call-analytics', dateFrom, dateTo],
    queryFn: ({ signal }) => videoCallsSvc.getVideoCallAnalytics({ dateFrom, dateTo }, { signal }),
    staleTime: 30_000,
  });

  // Server-narrowed to the signed-in user's own calls. Everything that only
  // means something across a team is dropped rather than relabelled.
  const selfOnly = data?.scope === 'self';

  // Only the People filter consumes the roster, and that filter is not drawn in
  // the own-calls view — so don't pull the staff list for someone who cannot
  // filter by it. Waiting for `data` matters: on the first render the scope is
  // not known yet, and `!selfOnly` alone would already have fired the request.
  const { data: roster = [] } = useQuery({
    queryKey: ['filter-options', 'user'],
    queryFn: () => getFilterOptions(['user']).then((r) => r.user ?? []),
    staleTime: 5 * 60_000,
    enabled: data !== undefined && !selfOnly,
  });

  const template = data?.template ?? null;
  const teamTrend = data?.trend.teamAverage ?? [];
  const userTrends = data?.trend.byUser ?? [];
  const byUser = useMemo<VideoCallAnalyticsUserRow[]>(() => data?.byUser ?? [], [data]);
  const total = selfOnly ? null : (data?.total ?? null);

  const metricOptions = useMemo(
    () => [
      { value: 'overall', label: 'Scorecard Average' },
      ...(template?.fields.map((f) => ({ value: String(f.scorecardFieldID), label: f.name })) ?? []),
    ],
    [template],
  );

  const peopleOptions = useMemo(
    () => [{ value: TEAM_ID, label: TEAM_LABEL }, ...roster],
    [roster],
  );

  // Nothing checked yet -> default to just the Team Average line.
  const activeSelection = selectedPeople.length > 0 ? selectedPeople : [TEAM_ID];

  const chartLines = useMemo(() => {
    // Own-calls view: one line, and `teamAverage` already holds only this
    // user's days, so it is their own average — labelled as such.
    if (selfOnly) {
      return [{ id: TEAM_ID, label: SELF_LABEL, color: LINE_COLORS[0], points: teamTrend }];
    }
    return activeSelection.map((id, i) => ({
      id,
      label: id === TEAM_ID ? TEAM_LABEL : (peopleOptions.find((o) => o.value === id)?.label ?? id),
      color: LINE_COLORS[i % LINE_COLORS.length],
      points: id === TEAM_ID ? teamTrend : (userTrends.find((u) => String(u.userID) === id)?.points ?? []),
    }));
  }, [selfOnly, activeSelection, peopleOptions, teamTrend, userTrends]);

  const chartData = useMemo(() => {
    const rows = new Map<string, Record<string, number | string>>();
    for (const line of chartLines) {
      for (const point of line.points) {
        const value = metricValue(point, metric);
        if (value === null) continue;
        const row = rows.get(point.date) ?? { date: point.date };
        row[line.id] = value;
        rows.set(point.date, row);
      }
    }
    return [...rows.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }, [chartLines, metric]);

  useEffect(() => {
    const coverage = selfOnly
      ? 'your own video calls'
      : `${byUser.length} team member${byUser.length !== 1 ? 's' : ''}`;
    updateAIContext({
      dataSummary: template
        ? `Scorecard analytics for ${coverage}, ${dateFrom} to ${dateTo}`
        : 'No active scorecard template configured',
    });
  }, [template, selfOnly, byUser.length, dateFrom, dateTo, updateAIContext]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 p-6 pb-0">
        <PageHeader
          variant="compact"
          title="Analytics"
          actions={
            <div className="flex items-center gap-2 text-sm">
              <div className="w-48">
                <Select
                  options={metricOptions}
                  value={metric}
                  onChange={(v) => setMetric(v ?? 'overall')}
                />
              </div>
              {!selfOnly && (
                <div className="w-56">
                  <SearchableMultiSelect
                    options={peopleOptions}
                    selectedValues={selectedPeople}
                    onSelectionChange={setSelectedPeople}
                    placeholder="People"
                    searchPlaceholder="Search people..."
                  />
                </div>
              )}
              {!selfOnly && selectedPeople.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedPeople([])}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                >
                  Reset ×
                </button>
              )}
              <span className="mx-1 h-5 w-px bg-slate-200" />
              <DatePicker
                ariaLabel="From"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(value) => setRange((r) => ({ ...r, dateFrom: value }))}
                wrapperClassName="w-40"
              />
              <span className="text-slate-400">to</span>
              <DatePicker
                ariaLabel="To"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(value) => setRange((r) => ({ ...r, dateTo: value }))}
                wrapperClassName="w-40"
              />
            </div>
          }
        />
      </div>

      <div className="min-h-0 flex-1 space-y-6 overflow-auto p-6 pt-4">
        {isLoading && <p className="py-12 text-center text-sm text-slate-400">Loading analytics…</p>}

        {!isLoading && !template && (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            No active scorecard template is configured for this account. Set one up under Admin →
            Account → Scorecards to start seeing analytics here.
          </div>
        )}

        {!isLoading && template && (
          <>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="mb-4 text-sm font-semibold text-slate-700">
                {metricOptions.find((m) => m.value === metric)?.label ?? 'Scorecard Average'}
              </h3>
              {chartData.length === 0 ? (
                <p className="py-12 text-center text-sm text-slate-400">
                  No scored calls in this date range yet.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tickFormatter={formatAxisDate} tick={{ fontSize: 12 }} />
                    <YAxis domain={[template.ratingMin, template.ratingMax]} tick={{ fontSize: 12 }} />
                    <Tooltip labelFormatter={formatAxisDate} />
                    <Legend />
                    {chartLines.map((line) => (
                      <Line
                        key={line.id}
                        type="monotone"
                        dataKey={line.id}
                        name={line.label}
                        stroke={line.color}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="border-b border-slate-200 px-5 py-4">
                <h3 className="text-sm font-semibold text-slate-700">
                  {selfOnly ? 'My Scores' : 'All Team Members'}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className={thCls}>Name</th>
                      <th className={thCls}>Overall Score</th>
                      {template.fields.map((f) => (
                        <th key={f.scorecardFieldID} className={thCls}>{f.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {total && (
                      <tr className="bg-slate-50 font-semibold">
                        <td className={nameTdCls}>Total</td>
                        <ScoreCell score={total.overallScore} ratingMax={template.ratingMax} />
                        {template.fields.map((f) => (
                          <ScoreCell
                            key={f.scorecardFieldID}
                            score={fieldScore(total.fields, f.scorecardFieldID)}
                            ratingMax={template.ratingMax}
                          />
                        ))}
                      </tr>
                    )}
                    {byUser.map((row) => (
                      <tr key={row.userID} className="hover:bg-slate-50">
                        <td className={nameTdCls}>
                          <div className="font-medium text-slate-800">{row.name}</div>
                          {row.title && <div className="text-xs text-slate-400">{row.title}</div>}
                        </td>
                        <ScoreCell score={row.overallScore} ratingMax={template.ratingMax} />
                        {template.fields.map((f) => (
                          <ScoreCell
                            key={f.scorecardFieldID}
                            score={fieldScore(row.fields, f.scorecardFieldID)}
                            ratingMax={template.ratingMax}
                          />
                        ))}
                      </tr>
                    ))}
                    {byUser.length === 0 && (
                      <tr>
                        <td
                          colSpan={2 + template.fields.length}
                          className="border border-slate-200 px-4 py-8 text-center text-sm text-slate-400"
                        >
                          No scored video calls in this date range yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default VideoCallAnalyticsPage;
